import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Download, Mail, CalendarDays, MapPin, Ticket as TicketIcon,
  User2, FileText, ExternalLink, ShieldCheck, AlertTriangle, Package, Send,
} from "lucide-react";
import {
  adminApi, adminTicketPdfUrl, adminOrderTicketsZipUrl,
  formatAuDate, centsToAUD,
} from "../../lib/events";
import { extractError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { useAuth } from "../../context/AuthContext";

const ACTIVE_STATUSES = new Set(["valid", "checked_in"]);

export default function AdminOrderDetail() {
  const { user } = useAuth();
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [dialog, setDialog] = useState(null); // {scope: "single"|"selected"|"all", ticketIds?: string[]}

  const load = () => {
    setLoading(true);
    adminApi.getOrder(orderId)
      .then((d) => { setData(d); setError(""); setSelected(new Set()); })
      .catch((e) => setError(extractError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [orderId]);

  const activeTickets = useMemo(
    () => (data?.tickets || []).filter((t) => ACTIVE_STATUSES.has(t.status)),
    [data]
  );
  const selectedList = useMemo(
    () => (data?.tickets || []).filter((t) => selected.has(t.id)),
    [data, selected]
  );
  const selectedActiveList = useMemo(
    () => selectedList.filter((t) => ACTIVE_STATUSES.has(t.status)),
    [selectedList]
  );

  const toggleTicket = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllActive = () => {
    setSelected((prev) => {
      const all = activeTickets.map((t) => t.id);
      if (all.every((id) => prev.has(id))) return new Set();
      return new Set([...prev, ...all]);
    });
  };

  if (!user?.is_admin) {
    return (
      <section className="p-8" data-testid="admin-order-forbidden">
        <div className="max-w-md mx-auto text-center">
          <AlertTriangle size={40} className="mx-auto text-[var(--gt-accent)] mb-3" />
          <h1 className="font-heading text-3xl font-black tracking-tighter mb-2">Admin only</h1>
          <p className="text-[var(--gt-ink-2)] text-sm mb-6">
            This page is restricted to platform administrators.
          </p>
          <Link to="/dashboard/events" className="gt-btn-ghost text-sm">Back</Link>
        </div>
      </section>
    );
  }

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center" data-testid="admin-order-loading">
      <Loader2 className="animate-spin text-[var(--gt-ink-2)]" size={20} />
    </div>;
  }
  if (error || !data) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center" data-testid="admin-order-error">
        <div className="gt-label mb-2">Order unavailable</div>
        <h1 className="font-heading text-2xl font-black tracking-tight mb-2">Cannot open this order</h1>
        <p className="text-sm text-[var(--gt-ink-2)] mb-4" data-testid="admin-order-error-msg">{error}</p>
        <Link to="/dashboard/admin?tab=orders" className="gt-btn-ghost text-sm"><ArrowLeft size={14}/> Back to orders</Link>
      </div>
    );
  }

  const { order, event, organiser, tickets, last_email } = data;
  const eligibleForResend = activeTickets.length > 0;

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="admin-order-view">
      <div className="flex items-center gap-2 text-xs text-[var(--gt-ink-3)]">
        <Link to="/dashboard/admin?tab=orders" className="hover:text-[var(--gt-accent)]"><ArrowLeft size={12}/> Back to orders</Link>
        <span>·</span>
        <ShieldCheck size={12} className="text-[var(--gt-accent)]"/>
        <span className="uppercase tracking-widest">Admin view</span>
      </div>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="gt-label mb-1">Order</div>
          <div className="font-mono text-sm break-all" data-testid="admin-order-id">{order.id}</div>
          <div className="text-[10px] text-[var(--gt-ink-3)] mt-1">Created {formatAuDate(order.created_at)} · Status <span className="font-semibold text-[var(--gt-ink)]" data-testid="admin-order-status">{order.status}</span></div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="gt-btn-primary text-sm py-2"
            data-testid="admin-order-download-all-btn"
            onClick={() => setDialog({ scope: "download_all" })}
            title="Download all valid tickets as a ZIP"
            disabled={activeTickets.length === 0}
          >
            <Package size={14}/> Download all valid tickets
          </button>
          <button
            type="button"
            className="gt-btn-secondary text-sm py-2"
            disabled={!eligibleForResend}
            data-testid="admin-order-resend-all-btn"
            onClick={() => setDialog({ scope: "all" })}
          >
            <Mail size={14}/> Resend all valid tickets
          </button>
        </div>
      </div>

      {/* Bulk actions bar */}
      {selected.size > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 border-2 border-[var(--gt-ink)] bg-[var(--gt-bg-marketing)] p-3"
          data-testid="admin-order-bulk-bar"
        >
          <div className="text-xs">
            <strong data-testid="admin-order-selected-count">{selected.size}</strong> selected
            {selected.size !== selectedActiveList.length && (
              <span className="ml-2 text-[var(--gt-ink-3)]">
                ({selectedActiveList.length} valid · {selected.size - selectedActiveList.length} excluded)
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="gt-btn-ghost text-xs"
              onClick={() => setSelected(new Set())}
              data-testid="admin-order-clear-selection"
            >Clear</button>
            <button
              type="button"
              className="gt-btn-ghost text-xs py-1.5"
              disabled={selectedActiveList.length === 0}
              onClick={() => setDialog({ scope: "download_selected", ticketIds: selectedActiveList.map((t) => t.id) })}
              data-testid="admin-order-download-selected-btn"
              title="Download selected valid tickets as ZIP"
            >
              <Download size={12}/> Download selected
            </button>
            <button
              type="button"
              className="gt-btn-secondary text-xs py-1.5"
              disabled={selectedActiveList.length === 0}
              onClick={() => setDialog({ scope: "selected", ticketIds: selectedActiveList.map((t) => t.id) })}
              data-testid="admin-order-resend-selected-btn"
            >
              <Mail size={12}/> Resend selected
            </button>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="gt-card space-y-4" data-testid="admin-order-summary">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="gt-label mb-1">Event</div>
            <div className="font-semibold" data-testid="admin-order-event-name">{event.title}</div>
            <div className="text-[var(--gt-ink-2)] text-xs flex items-start gap-1.5 mt-1">
              <CalendarDays size={13} className="mt-0.5"/>
              <span>{formatAuDate(event.start_at)}</span>
            </div>
            <div className="text-[var(--gt-ink-2)] text-xs flex items-start gap-1.5 mt-1">
              <MapPin size={13} className="mt-0.5"/>
              <span>{event.venue_name}{event.state ? `, ${event.state}` : ""}</span>
            </div>
          </div>
          <div>
            <div className="gt-label mb-1">Organiser</div>
            <div className="font-semibold" data-testid="admin-order-organiser-name">{organiser.business_name || organiser.name}</div>
            <div className="text-xs text-[var(--gt-ink-2)]" data-testid="admin-order-organiser-email">{organiser.email}</div>
            <div className="text-[10px] font-mono text-[var(--gt-ink-3)] break-all mt-1">{organiser.id}</div>
          </div>
          <div>
            <div className="gt-label mb-1">Buyer</div>
            <div className="font-semibold flex items-center gap-1.5" data-testid="admin-order-buyer-name">
              <User2 size={14}/> {order.buyer_name || "—"}
            </div>
            <div className="text-xs text-[var(--gt-ink-2)]" data-testid="admin-order-buyer-email">{order.buyer_email}</div>
            <div className="text-xs text-[var(--gt-ink-2)]" data-testid="admin-order-buyer-phone">
              {order.buyer_phone || <span className="italic text-[var(--gt-ink-3)]">No phone on file</span>}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs border-t border-[var(--gt-line)] pt-3">
          <div><div className="gt-label mb-0.5">Total paid</div><div className="font-mono font-bold" data-testid="admin-order-total">{centsToAUD(order.amount_total_cents)}</div></div>
          <div><div className="gt-label mb-0.5">Refunded</div><div className="font-mono">{centsToAUD(order.refunded_cents || 0)}</div></div>
          <div><div className="gt-label mb-0.5">Tickets</div><div className="font-mono">{tickets.length}</div></div>
          <div><div className="gt-label mb-0.5">Last email</div>
            <div className="text-xs" data-testid="admin-order-last-email">
              {last_email ? `${last_email.kind} → ${formatAuDate(last_email.created_at)}` : <span className="text-[var(--gt-ink-3)]">—</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Tickets table */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="gt-label">Tickets ({tickets.length})</div>
          <button
            type="button"
            className="text-xs text-[var(--gt-ink-2)] hover:text-[var(--gt-accent)] underline"
            onClick={toggleAllActive}
            disabled={activeTickets.length === 0}
            data-testid="admin-order-toggle-all-active"
          >
            {activeTickets.every((t) => selected.has(t.id)) && activeTickets.length > 0
              ? "Deselect all valid"
              : "Select all valid"}
          </button>
        </div>
        <div className="border-2 border-[var(--gt-ink)] bg-white overflow-x-auto" data-testid="admin-tickets-list">
          <table className="w-full text-sm">
            <thead className="bg-[var(--gt-bg-marketing)]">
              <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">
                <th className="p-2 w-8"></th>
                <th className="p-2">Code</th>
                <th className="p-2">Type</th>
                <th className="p-2">Seat</th>
                <th className="p-2">Attendee</th>
                <th className="p-2">Status</th>
                <th className="p-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--gt-line)]">
              {tickets.map((t) => {
                const isActive = ACTIVE_STATUSES.has(t.status);
                const seat = t.seat_label
                  ? `${t.section || ""}${t.row_label ? " Row " + t.row_label : ""} ${t.seat_label}`.trim()
                  : "GA";
                return (
                  <tr key={t.id} data-testid={`admin-ticket-row-${t.code}`} className={isActive ? "" : "opacity-60"}>
                    <td className="p-2 align-top">
                      <input
                        type="checkbox"
                        checked={selected.has(t.id)}
                        onChange={() => toggleTicket(t.id)}
                        disabled={!isActive}
                        aria-label={`Select ticket ${t.code}`}
                        data-testid={`admin-ticket-checkbox-${t.code}`}
                      />
                    </td>
                    <td className="p-2 font-mono text-xs">{t.code}</td>
                    <td className="p-2">{t.ticket_type_name}</td>
                    <td className="p-2 text-xs">{seat}</td>
                    <td className="p-2 text-xs">
                      <div>{t.buyer_name || "—"}</div>
                      <div className="text-[10px] text-[var(--gt-ink-3)]">{t.buyer_email}</div>
                    </td>
                    <td className="p-2">
                      <span
                        className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-[var(--gt-line)] rounded-sm"
                        data-testid={`admin-ticket-status-${t.code}`}
                      >
                        {t.status}
                      </span>
                    </td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <a
                        href={adminTicketPdfUrl(t.id)}
                        target="_blank" rel="noreferrer"
                        className="gt-btn-ghost text-[10px] mr-1"
                        data-testid={`admin-ticket-pdf-${t.code}`}
                        title="Download this ticket PDF"
                      >
                        <FileText size={12}/> PDF
                      </a>
                      <button
                        type="button"
                        className="gt-btn-ghost text-[10px]"
                        disabled={!isActive}
                        onClick={() => setDialog({ scope: "single", ticketIds: [t.id], ticket: t })}
                        data-testid={`admin-ticket-resend-${t.code}`}
                        title={isActive ? "Resend this ticket" : "Ticket is not active"}
                      >
                        <Send size={12}/> Resend
                      </button>
                    </td>
                  </tr>
                );
              })}
              {tickets.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-sm text-[var(--gt-ink-3)]">
                  No tickets on this order.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {dialog && dialog.scope === "download_all" && (
        <ConfirmDownloadDialog
          scope="all"
          count={activeTickets.length}
          event={data.event}
          order={data.order}
          onClose={() => setDialog(null)}
          onConfirm={() => {
            window.open(adminOrderTicketsZipUrl(data.order.id), "_blank");
            setDialog(null);
          }}
        />
      )}

      {dialog && dialog.scope === "download_selected" && (
        <ConfirmDownloadDialog
          scope="selected"
          count={dialog.ticketIds.length}
          event={data.event}
          order={data.order}
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            try {
              const { blob, headers } = await adminApi.downloadSelectedTickets(data.order.id, dialog.ticketIds, false);
              const cd = headers["content-disposition"] || "";
              const m = /filename="([^"]+)"/.exec(cd);
              const fname = (m && m[1]) || `GetTickets_SelectedTickets.zip`;
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url; a.download = fname; document.body.appendChild(a); a.click();
              URL.revokeObjectURL(url); a.remove();
              toast.success(`Downloaded ${dialog.ticketIds.length} ticket(s)`);
              setDialog(null);
            } catch (e) {
              toast.error(extractError(e));
            }
          }}
        />
      )}

      {dialog && ["single", "selected", "all"].includes(dialog.scope) && (
        <ResendDialog
          data={data}
          scope={dialog.scope}
          ticketIds={dialog.ticketIds}
          singleTicket={dialog.ticket}
          onClose={() => setDialog(null)}
          onSent={() => { setDialog(null); load(); }}
        />
      )}
    </div>
  );
}

// Sprint BF — bulk download confirmation.
function ConfirmDownloadDialog({ scope, count, event, order, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const label = scope === "all" ? "Download all valid tickets" : "Download selected tickets";
  const doConfirm = async () => {
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="admin-download-confirm-dialog">
        <DialogHeader>
          <DialogTitle>{label}</DialogTitle>
          <DialogDescription>Each ticket is packaged as an individual PDF inside a ZIP archive.</DialogDescription>
        </DialogHeader>
        <div className="border border-[var(--gt-line)] p-3 bg-[var(--gt-bg-marketing)] text-xs space-y-1">
          <div><span className="gt-label">Event</span>: <strong>{event.title}</strong></div>
          <div><span className="gt-label">Order</span>: <span className="font-mono">{order.id.slice(0, 8)}…</span></div>
          <div><span className="gt-label">Tickets in ZIP</span>: <strong data-testid="admin-download-count">{count}</strong></div>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <button type="button" className="gt-btn-ghost text-sm" onClick={onClose} disabled={busy} data-testid="admin-download-cancel-btn">Cancel</button>
          <button type="button" className="gt-btn-primary text-sm" onClick={doConfirm} disabled={busy || count === 0} data-testid="admin-download-confirm-btn">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Download size={14}/>} Download ZIP
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResendDialog({ data, scope, ticketIds, singleTicket, onClose, onSent }) {
  const { order, event } = data;
  const [mode, setMode] = useState("original"); // original | alternate
  const [altEmail, setAltEmail] = useState("");
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);

  const activeTickets = (data.tickets || []).filter((t) => ACTIVE_STATUSES.has(t.status));
  const ticketsIncluded = scope === "single"
    ? [singleTicket]
    : scope === "selected"
      ? activeTickets.filter((t) => ticketIds?.includes(t.id))
      : activeTickets;
  const excludedInvalid = scope === "all"
    ? (data.tickets || []).filter((t) => !ACTIVE_STATUSES.has(t.status)).length
    : 0;

  const buttonLabel = scope === "single"
    ? "Send Ticket"
    : scope === "selected"
      ? "Send Selected Tickets"
      : "Send All Valid Tickets";
  const title = scope === "single" ? "Resend ticket" : (scope === "selected" ? "Resend selected tickets" : "Resend all valid tickets");

  const canSend = (() => {
    if (sending) return false;
    if (ticketsIncluded.length === 0) return false;
    if (mode === "alternate") {
      if (!altEmail.trim() || !/^\S+@\S+\.\S+$/.test(altEmail)) return false;
      if (!reason.trim()) return false;
    }
    return true;
  })();

  const submit = async () => {
    setSending(true);
    try {
      const payload = {
        recipient_mode: mode,
        alternate_email: mode === "alternate" ? altEmail.trim() : null,
        reason: reason.trim() || null,
      };
      let result;
      if (scope === "single") {
        result = await adminApi.resendSingleTicket(singleTicket.id, payload);
      } else {
        result = await adminApi.resendOrderTickets(order.id, {
          ...payload,
          ticket_ids: scope === "selected" ? ticketIds : null,
        });
      }
      toast.success(`Sent ${result.tickets_sent} ticket(s) to ${result.recipient}`);
      onSent();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="admin-resend-dialog">
        <DialogHeader>
          <DialogTitle data-testid="admin-resend-dialog-title">{title}</DialogTitle>
          <DialogDescription>Confirm the details below before sending. QR codes remain unchanged.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="border border-[var(--gt-line)] rounded-sm p-3 bg-[var(--gt-bg-marketing)] text-xs space-y-1">
            <div><span className="gt-label">Event</span>: <strong data-testid="admin-resend-event">{event.title}</strong></div>
            <div><span className="gt-label">Order</span>: <span className="font-mono">{order.id.slice(0, 8)}…</span></div>
            {scope === "single" && singleTicket && (
              <>
                <div><span className="gt-label">Ticket holder</span>: {singleTicket.buyer_name || order.buyer_name}</div>
                <div><span className="gt-label">Ticket type</span>: {singleTicket.ticket_type_name}</div>
                {singleTicket.seat_label && (
                  <div><span className="gt-label">Seat</span>: {singleTicket.section || ""} {singleTicket.row_label ? "Row " + singleTicket.row_label + " " : ""}{singleTicket.seat_label}</div>
                )}
                <div><span className="gt-label">Ticket code</span>: <span className="font-mono">{singleTicket.code}</span></div>
              </>
            )}
            {scope !== "single" && (
              <>
                <div><span className="gt-label">Tickets included</span>: <strong data-testid="admin-resend-count">{ticketsIncluded.length}</strong></div>
                {excludedInvalid > 0 && (
                  <div className="text-[var(--gt-accent)]">
                    <span className="gt-label">Excluded (invalid)</span>: {excludedInvalid}
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <div className="gt-label mb-2">Send to</div>
            <label className="flex items-center gap-2 py-1 cursor-pointer">
              <input
                type="radio"
                checked={mode === "original"}
                onChange={() => setMode("original")}
                data-testid="admin-resend-mode-original"
              />
              <span>Original customer email — <strong>{order.buyer_email || "(none on file)"}</strong></span>
            </label>
            <label className="flex items-center gap-2 py-1 cursor-pointer">
              <input
                type="radio"
                checked={mode === "alternate"}
                onChange={() => setMode("alternate")}
                data-testid="admin-resend-mode-alternate"
              />
              <span>Another email address</span>
            </label>
            {mode === "alternate" && (
              <div className="mt-2 space-y-2 pl-6">
                <input
                  type="email"
                  className="gt-input w-full text-sm"
                  placeholder="customer-assistant@example.com"
                  value={altEmail}
                  onChange={(e) => setAltEmail(e.target.value)}
                  data-testid="admin-resend-alt-email"
                />
                <div className="text-[11px] text-[var(--gt-ink-3)]">
                  Order email <strong>{order.buyer_email}</strong> stays unchanged. The alternate destination is recorded in the audit log.
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="gt-label mb-1">Reason / internal note{mode === "alternate" ? " (required)" : " (optional)"}</div>
            <textarea
              className="gt-input w-full text-sm"
              rows={2}
              placeholder="e.g. Customer support call — buyer requested resend"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              data-testid="admin-resend-reason"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-2">
          <button
            type="button"
            onClick={onClose}
            className="gt-btn-ghost text-sm"
            disabled={sending}
            data-testid="admin-resend-cancel-btn"
          >Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSend}
            className="gt-btn-primary text-sm"
            data-testid="admin-resend-submit-btn"
          >
            {sending ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>} {buttonLabel}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
